import { useEffect, useRef, useState } from "react";
import anime from "animejs";
import {
  OVERLAY_FADE_DURATION,
  CONTENT_ANIM_DURATION,
} from "../Components/UserInterface/secondary/Menu/paytableConfig";

/**
 * useOverlayFade — Shared mount/fade lifecycle for the secondary-UI HTML
 * overlays (paytable, game rules, replay/history).
 *
 * Replaces the per-overlay setTimeout pattern: animejs drives `opacity` on the
 * RAF ticker and its `complete` callback performs the unmount, so nothing
 * depends on a timer firing on schedule. Same approach as BuyBonusPopup.
 *
 * Opening is deliberately two steps — the DOM node doesn't exist until
 * `shouldRender` has committed, so the fade-in runs in a mount effect rather
 * than being guessed at with a delay.
 *
 * Also owns the iOS-overlay guard fade: while the iOS fullscreen spine is up the
 * overlay animates to 0 and back, instead of an imperative style write that
 * would fight a running tween.
 *
 * All opacity changes go through one tween (`animRef`) and always target from
 * the node's actual current opacity rather than a hardcoded value — mount and
 * guard toggles can land on the same commit (opening while already `isHidden`,
 * or the guard flipping right as a close starts), and a second independent
 * tween on top of a running one is what caused a visible flash on open and a
 * stuck-mid-fade opacity on rapid close→reopen.
 *
 * @param {object}   params
 * @param {boolean}  params.active   Redux flag for "should this overlay be open"
 * @param {boolean}  [params.isHidden]  from useIosOverlayGuard()
 * @param {Function} [params.onClosed]  run once the close fade completes
 * @param {number}   [params.fadeInDelay]  ms to wait before the initial open
 *   fade-in starts (defaults to the shared OVERLAY_FADE_DURATION). Only
 *   applies to the first fade-in after mount, not later guard toggles.
 * @returns {{ shouldRender: boolean, overlayRef: React.RefObject }}
 *   Spread `opacity: 0` on the root element's style and attach `overlayRef`;
 *   render nothing while `shouldRender` is false.
 */
export const useOverlayFade = ({
  active,
  isHidden = false,
  onClosed,
  fadeInDelay = OVERLAY_FADE_DURATION,
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const overlayRef = useRef(null);
  const prevActiveRef = useRef(false);
  // Single in-flight tween — every effect below pauses this before starting a
  // new one, so at most one anime instance ever touches the node's opacity.
  const animRef = useRef(null);
  // True once the initial open fade-in has completed, so later iOS-guard
  // toggles animate immediately instead of re-applying the mount delay.
  const mountFadeDoneRef = useRef(false);

  // Keep the latest callback without making it an effect dependency (a new
  // function identity each render would otherwise restart the close fade).
  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;
  // Same for fadeInDelay — callers pass a literal so it never actually
  // changes, but reading it via ref avoids listing it as a dep for no reason.
  const fadeInDelayRef = useRef(fadeInDelay);
  fadeInDelayRef.current = fadeInDelay;

  // Open → mount (the fade-in itself runs in the effect below, once the DOM
  // node exists). Close → fade out, then unmount from `complete`.
  useEffect(() => {
    if (active && !prevActiveRef.current) {
      setShouldRender(true);
    } else if (!active && prevActiveRef.current) {
      if (animRef.current) animRef.current.pause();
      if (!overlayRef.current) {
        // No node to animate (shouldn't happen — mount always commits before
        // active can flip false — but never leave shouldRender stuck true
        // with nothing left to unmount it).
        mountFadeDoneRef.current = false;
        setShouldRender(false);
        onClosedRef.current?.();
      } else {
        animRef.current = anime({
          targets: overlayRef.current,
          opacity: 0,
          duration: CONTENT_ANIM_DURATION,
          easing: "easeInQuad",
          complete: () => {
            mountFadeDoneRef.current = false;
            setShouldRender(false);
            onClosedRef.current?.();
          },
        });
      }
    }
    prevActiveRef.current = active;
  }, [active]);

  // Drive opacity toward its target: 1 while open and visible, 0 while the
  // iOS fullscreen spine covers it. Re-runs on every `active`/`shouldRender`/
  // `isHidden` change (not just mount) so a reopen mid-close-fade also lands
  // here and re-targets from wherever the paused tween left off.
  useEffect(() => {
    if (!active || !shouldRender || !overlayRef.current) return;
    if (animRef.current) animRef.current.pause();
    const isFirstFadeIn = !mountFadeDoneRef.current;
    animRef.current = anime({
      targets: overlayRef.current,
      opacity: isHidden ? 0 : 1,
      duration: CONTENT_ANIM_DURATION,
      delay: isFirstFadeIn ? fadeInDelayRef.current : 0,
      easing: isHidden ? "easeInQuad" : "easeOutQuad",
      complete: () => {
        if (isFirstFadeIn) mountFadeDoneRef.current = true;
      },
    });
  }, [active, shouldRender, isHidden]);

  // Stop any in-flight tween on unmount so anime never writes to a node React
  // has already torn down.
  useEffect(() => {
    return () => {
      if (animRef.current) animRef.current.pause();
    };
  }, []);

  return { shouldRender, overlayRef };
};
